import { useState } from 'react'
import { Panel, Chip, Button, Modal, FormRow, Textarea, Select, ConfirmDialog, useToast } from '../ds/index'
import { useApi } from '../lib/useApi'
import { post } from '../lib/api'
import { money, dateShort, ago } from '../lib/format'

type Pause = { id: string; kind: string; reason: string; started_at: string; ended_at: string | null; end_note: string | null; started_by_name: string | null; ended_by_name: string | null }
type Chargeback = {
  id: string; amount_cents: number; fee_cents: number; withdrawn_cents: number; reason: string | null; stripe_status: string | null; stripe_case_type: string | null
  status: 'open' | 'repaid' | 'won' | 'our_error'; note: string | null; created_at: string; resolved_at: string | null
  // Worked out on the server (lib/chargebacks.js owed()): what Stripe took, what is owed now, and what is left over after settling.
  taken_cents: number; due_cents: number; repaid_cents: number; owed_cents: number; waived_cents: number; short_cents: number; overpaid_cents: number
  pending_return: boolean
}
type Status = {
  offers?: { level: string; answer: string | null; created_at: string }[]; self_pauses_this_month?: number
  billing_cycle: 'monthly' | 'weekly'; paused: boolean; pauses: Pause[]; chargebacks: Chargeback[]
  trial: { running: boolean; ends_at: string; cap_cents: number; used_cents: number; full: boolean; billed_at: string | null; cancelled_at: string | null; can_cancel: boolean; deposit_cents: number | null } | null
  tab: { started_at: string | null; amount_cents: number; skip_period: string | null; next: { period: string; date: string; skipped: boolean } | null } | null
}

const KIND: Record<string, string> = {
  payment_failed: 'Payment failed', no_card: 'No card on file', chargeback: 'Chargeback', cause: 'Paused for cause', self: 'Paused by the business',
}
const SETTLED: Record<string, string> = { repaid: 'Repaid', won: 'Won at the bank', our_error: 'Our mistake, nothing owed' }
// Where the dispute stands at Stripe, in plain words.
const AT_STRIPE: Record<string, string> = {
  warning_needs_response: 'Inquiry from the bank', warning_under_review: 'Inquiry under review', warning_closed: 'Inquiry closed',
  needs_response: 'Needs a response in Stripe', under_review: 'Under review at the bank', won: 'Bank decided for us', lost: 'Bank decided for them', prevented: 'Stopped before it began',
}
const atStripe = (c: Chargeback) => c.stripe_status === 'prevented' && c.stripe_case_type === 'resolution'
  ? 'Refunded to the cardholder before it became a chargeback' : (c.stripe_status ? AT_STRIPE[c.stripe_status] || c.stripe_status : '')

/**
 * Weekly billing on the company's side (Business Terms v1.3): whether this
 * business is getting leads and why not, its monthly tab, and its chargebacks.
 */
export function ClientBillingStatus({ clientId }: { clientId: string }) {
  const { data, reload } = useApi<Status>(`/api/clients/${clientId}/billing-status`)
  const toast = useToast()
  const [pausing, setPausing] = useState(false)
  const [ending, setEnding] = useState<Pause | null>(null)
  const [settling, setSettling] = useState<Chargeback | null>(null)
  const [starting, setStarting] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  if (!data || data.billing_cycle !== 'weekly') return null
  const open = data.pauses.filter((p) => !p.ended_at)
  const past = data.pauses.filter((p) => p.ended_at).slice(0, 5)

  const startTab = async () => {
    try {
      const r = await post<{ result: string }>(`/api/clients/${clientId}/start-tab`, {})
      toast(r.result === 'succeeded' ? 'Tab started: the first month is charged' : r.result === 'processing' ? 'Tab started: the bank payment is processing' : 'Tab started, but the charge did not go through. Leads are paused until it is paid.', r.result === 'failed' ? 'neg' : 'pos')
      reload()
    } catch (e) { toast((e as Error).message, 'neg') } finally { setStarting(false) }
  }

  return (
    <Panel>
      <div className="panel-header">
        <h2 className="panel-title">Leads and weekly billing</h2>
        <Chip tone={open.length ? 'neg' : 'pos'} dot>{open.length ? 'Leads paused' : 'Getting leads'}</Chip>
      </div>
      <div className="stack-tight">
        {open.map((p) => (
          <div key={p.id} className="spread wrap-gap" style={{ alignItems: 'center' }}>
            <div><strong>{KIND[p.kind] || p.kind}</strong> <span className="muted">since {dateShort(p.started_at)}{p.started_by_name ? `, by ${p.started_by_name}` : ''}</span><div className="muted" style={{ fontSize: 13 }}>{p.reason}</div></div>
            {p.kind !== 'chargeback' && <Button variant="secondary" onClick={() => setEnding(p)}>Start leads again</Button>}
          </div>
        ))}
        {data.trial && (
          <div className="spread wrap-gap" style={{ alignItems: 'center' }}>
            <div>
              <strong>Trial</strong>{' '}
              <span className="muted">
                {data.trial.running ? `until ${dateShort(data.trial.ends_at)} · ${money(data.trial.used_cents)} of ${money(data.trial.cap_cents)} of leads${data.trial.full ? ' (allowance used: no new leads until it ends)' : ''}`
                  : data.trial.cancelled_at ? `cancelled ${dateShort(data.trial.cancelled_at)}, deposit refunded`
                    : data.trial.billed_at ? `ended, invoiced ${dateShort(data.trial.billed_at)}` : 'ended, invoicing at the next pass'}
              </span>
            </div>
            {data.trial.can_cancel && <Button variant="secondary" onClick={() => setCancelling(true)}>Cancel trial and refund</Button>}
          </div>
        )}
        <div className="cost-rows">
          <div className="cost-row"><span>Monthly tab</span><span className="num">{data.tab ? money(data.tab.amount_cents) : ''}</span></div>
          <div className="cost-row"><span className="muted">Started</span><span className="num muted">{data.tab?.started_at ? dateShort(data.tab.started_at) : 'Not yet'}</span></div>
          {data.tab?.next && <div className="cost-row"><span className="muted">Next charge</span><span className="num muted">{data.tab.next.skipped ? `Skipped (${data.tab.next.period})` : dateShort(`${data.tab.next.date}T12:00:00`)}</span></div>}
        </div>
        {data.chargebacks.length > 0 && (
          <div className="stack-tight">
            <strong>Chargebacks</strong>
            {data.chargebacks.map((c) => (
              <div key={c.id} className="spread wrap-gap" style={{ alignItems: 'center' }}>
                <div>
                  <span className="num">{money(c.amount_cents)}</span>{c.fee_cents ? <span className="muted"> + {money(c.fee_cents)} Stripe fee</span> : null}
                  <span className="muted"> · {c.reason || 'no reason given'} · {ago(c.created_at)}{c.stripe_status ? ` · ${atStripe(c)}` : ''}</span>
                  <div className="muted" style={{ fontSize: 13 }}>
                    {c.status === 'open'
                      ? (c.pending_return ? 'The bank decided for us; waiting for Stripe to return the money. Nothing is asked of them until it has, and then it settles by itself unless Stripe kept its fee.'
                        : c.owed_cents > 0 ? `Owes ${money(c.owed_cents)}${c.repaid_cents ? ` after ${money(c.repaid_cents)} repaid` : ''}. They can pay it from their portal; paid in full, this settles by itself.`
                          : 'Nothing taken back yet, so nothing to pay. It settles by itself if the bank closes it for us.')
                      : [c.repaid_cents ? `${money(c.repaid_cents)} repaid` : null, c.waived_cents ? `${money(c.waived_cents)} waived` : null,
                        c.short_cents ? `Stripe took ${money(c.short_cents)} more after it was repaid` : null].filter(Boolean).join(' · ')}
                  </div>
                  {c.overpaid_cents > 0 && <div style={{ fontSize: 13, color: 'var(--neg)' }}>They have paid {money(c.overpaid_cents)} more than is owed on this chargeback. Refund it in Stripe or credit it.</div>}
                </div>
                {c.status === 'open' ? <Button variant="secondary" onClick={() => setSettling(c)}>Settle</Button> : <Chip>{SETTLED[c.status]}</Chip>}
              </div>
            ))}
          </div>
        )}
        {(data.self_pauses_this_month || 0) > 0 && (
          <p className="muted" style={{ margin: 0 }}>
            Paused by themselves {data.self_pauses_this_month} time{data.self_pauses_this_month === 1 ? '' : 's'} this month.
            {data.offers?.length ? ` Pause rule: ${data.offers.map((o) => `${{ offer: 'help offered', again: 'help offered again', final: 'final notice', after_final: 'paused after the final notice' }[o.level] || o.level}${o.answer ? ` (${o.answer === 'interested' ? 'wants to hear more' : 'declined'})` : ''} ${dateShort(o.created_at)}`).join('; ')}.` : ''}
          </p>
        )}
        {past.length > 0 && (
          <details>
            <summary className="muted" style={{ cursor: 'pointer' }}>Earlier pauses</summary>
            {past.map((p) => <div key={p.id} className="muted" style={{ fontSize: 13, marginTop: 'var(--s-2)' }}>{KIND[p.kind] || p.kind}, {dateShort(p.started_at)} to {dateShort(p.ended_at)}{p.end_note ? `: ${p.end_note}` : ''}</div>)}
          </details>
        )}
        <div className="row-tight" style={{ flexWrap: 'wrap' }}>
          {!data.tab?.started_at && <Button onClick={() => setStarting(true)}>Start tab and charge the first month</Button>}
          {!open.some((p) => p.kind === 'cause') && <Button variant="ghost" onClick={() => setPausing(true)}>Pause for cause</Button>}
        </div>
      </div>

      {starting && <ConfirmDialog title="Start the monthly tab?" confirmLabel={`Charge ${money(data.tab?.amount_cents ?? 0)}`}
        body={`The first month, ${money(data.tab?.amount_cents ?? 0)}, is charged to their card now and added as tab credit. After that it is charged once a month on this day (Business Terms 5.1).`}
        onConfirm={startTab} onCancel={() => setStarting(false)} />}
      {pausing && <PauseForCause clientId={clientId} onClose={() => setPausing(false)} onDone={reload} />}
      {ending && <EndPause p={ending} onClose={() => setEnding(null)} onDone={reload} />}
      {settling && <Settle c={settling} onClose={() => setSettling(null)} onDone={reload} />}
      {cancelling && data.trial && <CancelTrial clientId={clientId} deposit={data.trial.deposit_cents ?? 0} onClose={() => setCancelling(false)} onDone={reload} />}
    </Panel>
  )
}

function PauseForCause({ clientId, onClose, onDone }: { clientId: string; onClose: () => void; onDone: () => void }) {
  const toast = useToast()
  const [reason, setReason] = useState('')
  const go = async () => {
    try { await post(`/api/clients/${clientId}/pause`, { reason }); toast('Leads paused. The business has been told why.', 'pos'); onDone(); onClose() }
    catch (e) { toast((e as Error).message, 'neg') }
  }
  return (
    <Modal title="Pause new leads for cause" onClose={onClose} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={go}>Pause leads</Button></>}>
      <p style={{ marginTop: 0 }}>For a breach, a lapsed license, or a credible complaint (Business Terms 13.6). The business is emailed this reason and sees it in its portal.</p>
      <FormRow label="Why (the business sees this)" htmlFor="pz-reason"><Textarea id="pz-reason" rows={3} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} /></FormRow>
    </Modal>
  )
}

function EndPause({ p, onClose, onDone }: { p: Pause; onClose: () => void; onDone: () => void }) {
  const toast = useToast()
  const [note, setNote] = useState('')
  const go = async () => {
    try { await post(`/api/pauses/${p.id}/end`, { note }); toast('Leads start again', 'pos'); onDone(); onClose() }
    catch (e) { toast((e as Error).message, 'neg') }
  }
  return (
    <Modal title="Start leads again" onClose={onClose} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={go}>Start leads</Button></>}>
      <p style={{ marginTop: 0 }}>{p.reason}</p>
      <FormRow label={p.kind === 'cause' ? 'Note (optional)' : 'How it was fixed (for example, paid by check)'} htmlFor="ep-note">
        <Textarea id="ep-note" rows={2} maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} />
      </FormRow>
    </Modal>
  )
}

function Settle({ c, onClose, onDone }: { c: Chargeback; onClose: () => void; onDone: () => void }) {
  const toast = useToast()
  const [status, setStatus] = useState('repaid')
  const [note, setNote] = useState('')
  const go = async () => {
    try { await post(`/api/chargebacks/${c.id}/resolve`, { status, note, owed_cents: c.owed_cents }); toast('Settled. Leads start again unless something else is holding them.', 'pos'); onDone(); onClose() }
    // Stripe may have changed what is owed since this opened: close and reload so the new amount is what gets settled.
    catch (e) { toast((e as Error).message, 'neg'); onDone(); onClose() }
  }
  return (
    <Modal title={`Settle the ${money(c.amount_cents)} chargeback`} onClose={onClose} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={go}>Settle</Button></>}>
      <p style={{ marginTop: 0 }}>
        The amount stays owed, and the Stripe fee too unless the charge was our mistake (Business Terms 6.5).
        {c.taken_cents > 0 ? ` Stripe has taken ${money(c.taken_cents)} for it${c.withdrawn_cents && c.fee_cents ? ` (${money(c.withdrawn_cents)} disputed plus its ${money(c.fee_cents)} fee)` : ''}.` : ' Stripe has not taken anything back yet.'}
        {c.owed_cents > 0 ? ` ${money(c.owed_cents)} is owed now; they can pay it from their portal, and paying in full settles this by itself.` : ''}
      </p>
      <FormRow label="How it was settled" htmlFor="cb-status">
        <Select id="cb-status" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="repaid">{c.owed_cents > 0 ? `They repaid ${money(c.owed_cents)} another way (a check, cash)` : 'They repaid it'}</option>
          <option value="won">{c.owed_cents > 0 ? `The bank decided for us: waive the ${money(c.owed_cents)} left` : 'The bank decided for us'}</option>
          <option value="our_error">It was our mistake: nothing owed</option>
        </Select>
      </FormRow>
      <FormRow label="Note" htmlFor="cb-note"><Textarea id="cb-note" rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} /></FormRow>
    </Modal>
  )
}

function CancelTrial({ clientId, deposit, onClose, onDone }: { clientId: string; deposit: number; onClose: () => void; onDone: () => void }) {
  const toast = useToast()
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const go = async () => {
    setBusy(true)
    try { await post(`/api/clients/${clientId}/trial/cancel`, { reason }); toast(`Trial cancelled; ${money(deposit)} refunded to their card`, 'pos'); onDone(); onClose() }
    catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }
  return (
    <Modal title="Cancel this trial and refund the deposit" onClose={onClose} footer={<><Button variant="secondary" onClick={onClose}>Keep it</Button><Button onClick={go} disabled={busy || reason.trim().length < 3}>{busy ? 'Cancelling' : `Cancel and refund ${money(deposit)}`}</Button></>}>
      <p style={{ marginTop: 0 }}>The {money(deposit)} deposit goes back to their card, SPEC's fee with it. Their trial leads are not billed, new leads stop, their card is removed, and they are emailed.</p>
      <FormRow label="Why (for the record)" htmlFor="ct-reason"><Textarea id="ct-reason" rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} /></FormRow>
    </Modal>
  )
}
